import { useCallback } from 'react'
import { Star, StarOff } from 'lucide-react'
import { useToast } from '../admin/primitives.js'
import type { GroupeMenu } from './menu-contextuel.js'
import { ajouterFavori, etatFavoris, nomDeFavori, retirerFavori } from './favoris.js'
import { planFavori } from './menu-favori-modele.js'

/**
 * « AJOUTER AUX FAVORIS » DANS LES MENUS CLIC DROIT (lot 6, 24/10/2026) —
 * l'aide commune des menus par domaine (portes et personnes Access, salariés,
 * soirées et nuits, salles, appareils, tuiles).
 *
 * Le même geste que l'étoile de l'en-tête (BoutonFavori) : les mêmes
 * `ajouterFavori` et `retirerFavori` de `favoris.ts`, donc la même session,
 * le même CSRF, la même file d'attente et le même toast (« Annuler » après un
 * retrait). Aucun droit dédié (décision d'Hugo) : chaque menu n'appelle
 * l'aide que là où il propose déjà d'OUVRIR la page, sous le droit de celle-ci.
 *
 * Construit à l'OUVERTURE du menu, depuis l'état en mémoire (`etatFavoris`) :
 * aucune lecture réseau ici, le serveur n'est appelé qu'au clic. La décision
 * (absent, « Retirer », « Ajouter » grisé ou non) vit dans
 * `menu-favori-modele.ts` ; un groupe vide est retiré par le menu lui-même.
 *
 * Le nom est celui que le menu affiche déjà (le nom de la porte, du salarié,
 * de la soirée, ROOM 1…), jamais un e-mail ; c'est un instantané, comme
 * l'étoile. Pas de renommage, et jamais sur un incident.
 *
 *     const favori = useGroupeFavori()
 *     groupes.push(favori(cheminPorte(p.id), p.nom ?? 'Porte'))
 */
export function useGroupeFavori(): (href: string, libelle: string) => GroupeMenu {
  const toast = useToast()
  return useCallback((href: string, libelle: string): GroupeMenu => {
    const plan = planFavori(etatFavoris(), href)
    if (plan.sorte === 'aucun') return { actions: [] }
    if (plan.sorte === 'retirer') {
      const f = plan.favori
      return { actions: [{
        id: 'favori-retirer', libelle: 'Retirer des favoris', icone: <StarOff size={15} />,
        agir: () => { void retirerFavori(f, toast) },
      }] }
    }
    const chemin = plan.chemin
    return { actions: [{
      id: 'favori-ajouter', libelle: 'Ajouter aux favoris', icone: <Star size={15} />,
      ...(plan.indisponible ? { indisponible: plan.indisponible } : {}),
      agir: () => { void ajouterFavori({ path: chemin, label: nomDeFavori(libelle, 'Page de Gestion') }, toast) },
    }] }
  }, [toast])
}
